/**
 * 聊天模块 API — 全部通过 IPC → Rust → ChatService 链路
 * 禁止 HTTP 直连 ChatService（NFR-SEC-01~02）
 */
import { ipcCall, type IPCOptions } from '@/composables/useIPC'
import { IpcCommand } from '@/types/ipc'
import { env } from '@/utils/env'

/** 获取联系人列表 */
export function getChatContacts(params: ChatContactListParams) {
  return ipcCall<ChatContactListRes>(IpcCommand.ChatGetContacts, { params })
}

/** 扫描 ChatService 在日期窗口内确实有聊天的会话，只返回联系人 ID 和最近活跃时间。 */
export function getChatActiveSessions(params: ChatActiveSessionListParams) {
  return ipcCall<ChatActiveSessionListRes>(IpcCommand.ChatGetActiveSessions, { params })
}

/** 获取聊天记录（游标分页） */
export function getChatMessages(params: ChatMessageListParams, options?: IPCOptions) {
  return ipcCall<ChatMessageListRes>(IpcCommand.ChatGetMessages, { params }, options)
}

/** 获取 ChatService 运行状态 */
export function getChatServiceStatus(requestId?: string) {
  return ipcCall<ChatServiceStatus>(
    IpcCommand.ChatGetServiceStatus,
    requestId ? { requestId } : undefined,
  )
}

/** 按微信号(alias)查询解密账号；账号未设置 alias 时接受原始 wxId。 */
export function findChatServiceAccountByRealWechatId(realWechatId: string) {
  return ipcCall<ChatServiceAccount | null>(IpcCommand.ChatFindAccountByRealWechatId, {
    realWechatId,
  })
}

/** 强制实时读取指定已解密账号的个人微信好友，不使用联系人缓存。 */
export function getChatContactsForImport(accountAlias: string, ownerWxId: string) {
  return ipcCall<ChatContactImportResponse>(IpcCommand.ChatGetContactsForImport, {
    accountAlias,
    ownerWxId,
  })
}

/** 读取指定微信账号在某日首次检测到的好友。 */
export function getChatFriendDetections(ownerWxId: string, date: string) {
  return ipcCall<ChatFriendDetectionResponse>(IpcCommand.ChatGetFriendDetections, {
    ownerWxId,
    date,
  })
}

/** 按 OCR 昵称为联系人候选打分（用于 picker 排序） */
export function rankChatContactsByNickname(
  ocrName: string,
  candidates: ChatContactRankCandidate[],
) {
  return ipcCall<ChatContactRankScore[]>(
    IpcCommand.ChatRankContactsByNickname,
    {
      ocrName,
      candidates,
    },
    { silent: true },
  )
}

/** 按 OCR 昵称召回低阈值候选（用于补充 picker 候选池） */
export function suggestChatContactsByNickname(ocrName: string, isGroup: boolean) {
  return ipcCall<SuggestedContact[]>(
    IpcCommand.ChatSuggestContactsByNickname,
    {
      ocrName,
      isGroup,
    },
    { silent: true },
  )
}

/** 手动触发聊天数据同步 */
export function triggerChatSync() {
  if (env.PROD && env.MODE === 'production') {
    return Promise.reject(new Error('正式环境不支持手动触发聊天同步'))
  }
  return ipcCall<ChatSyncStatus>(IpcCommand.ChatTriggerSync)
}

export function isChatUploadDiagnosticsEnabled() {
  const appEnv = (env.APP_ENV || env.MODE || '').trim().toLowerCase()
  return env.DEV && ['development', 'dev', 'local'].includes(appEnv)
}

/** 客户聊天上传诊断状态 */
export function getChatUploadDiagnosticsStatus(requestId?: string) {
  return ipcCall<ChatUploadDiagnosticsStatus>(
    IpcCommand.ChatUploadDiagnosticsStatus,
    requestId ? { requestId } : undefined,
  )
}

/** 开发模式客户聊天上传诊断控制 */
export function controlChatUploadDiagnostics(
  action: ChatUploadDiagnosticsAction,
  reason?: string,
  requestId?: string,
) {
  if (!isChatUploadDiagnosticsEnabled()) {
    return Promise.reject(new Error('正式环境不支持上传诊断控制'))
  }
  const request: Record<string, string> = {
    action,
    reason: reason || 'dev-mode diagnostics',
  }
  if (requestId) request.requestId = requestId
  return ipcCall<ChatUploadDiagnosticsControlResult>(IpcCommand.ChatUploadDiagnosticsControl, {
    request,
  })
}

/** 获取朋友圈动态列表（Epic 11 2.3）*/
export function getSnsFeeds(params: SnsFeedListParams) {
  return ipcCall<SnsFeedListRes>(IpcCommand.ChatGetSnsFeeds, { params })
}
